import { DashboardORM } from '@models/dashboard';
import { requireConfirmation } from '@services/alert/alert.service';
import toast from '@services/toast.service';
import { useDashboardNotifications } from '@stores/data.store';
import React, { useCallback, useState } from 'react';
import { Form, Button, Table, Badge, InputGroup, Alert } from 'react-bootstrap';


// Define the props for the component
interface Props {
  dashboardId: string;
  currentUser: string;
}

export const DashboardNotificationsEditor = (props: Props) => {
  const { dashboardId, currentUser } = props;
  
  // State for the new email input
  const [newEmail, setNewEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: dn, isLoading, refetch } = useDashboardNotifications(dashboardId);
  
  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Handle form submission for adding a new user
  const handleAddUser = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if email is valid
    if (!emailRegex.test(newEmail)) {
      setIsValidEmail(false);
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (!dn) {
      return;
    }
    
    // Check if user already exists
    if (dn.email_users.some((user: any) => user.email.toLowerCase() === newEmail.toLowerCase())) {
      setIsValidEmail(false);
      setErrorMessage('This email is already in the notification list');
      return;
    }
    
    // Call the onAddUser callback
    await DashboardORM.addNotificationUser(dashboardId as string, newEmail);
    refetch();
    
    // Reset the form
    setNewEmail('');
    setIsValidEmail(true);
    setErrorMessage('');
  }, [dashboardId, dn, newEmail]);
  
  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmail(e.target.value);
    setIsValidEmail(true);
    setErrorMessage('');
  };


  const handleRemoveUser = useCallback(async (email: string) => {
        await DashboardORM.removeNotificationUser(dashboardId as string, email);
        refetch();
  }, [dashboardId]);

  const confirmSendNow = useCallback(async () => {
    if (!dn) {
      return;
    }

    const confirmed = await requireConfirmation(`Are you sure you want to send to ${dn.email_users.length} users?`, 'Send Notifications', 'Send', 'Cancel');
    if (confirmed) {
        // setPageDirty(false)
        await DashboardORM.sendNotificationsNow(dashboardId as string);
        toast('info', 'Notifications Job Started', 'It may take a few minutes to complete.');
    }
  }, [dn, dashboardId]);

  
  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="dashboard-notifications-editor">
      <Form onSubmit={handleAddUser} className="mb-4">
        <Form.Group>
          <Form.Label>Add Notification Recipient</Form.Label>
          <InputGroup>
            <Form.Control
              type="email"
              placeholder="Enter email address"
              value={newEmail}
              onChange={handleEmailChange}
              isInvalid={!isValidEmail}
            />
            <Button variant="primary" type="submit">
              Add Recipient
            </Button>
            <Form.Control.Feedback type="invalid">
              {errorMessage}
            </Form.Control.Feedback>
          </InputGroup>
          <Form.Text className="text-muted">
            Users will receive notifications of scheduled dashboard builds.
          </Form.Text>
        </Form.Group>
      </Form>
      
      {!!dn && dn.email_users.length > 0 ? (<>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Status</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {dn.email_users.map((user: any) => (
              <tr key={user.email}>
                <td>
                  {user.unsubscribed ? (
                    <Badge bg="warning">Unsubscribed</Badge>
                  ) : (
                    <Badge bg="success">Active</Badge>
                  )}
                </td>
                <td>
                  {user.email}
                </td>
        
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveUser(user.email)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex justify-content-end">
          <Button className='btn btn-sm' variant="light" onClick={confirmSendNow}>
              Send Now
        </Button>
        </div>
        
        </>
      ) : (
        <Alert variant="info">
          No notification recipients have been added yet.
        </Alert>
      )}
    </div>
  );
};